import React, {useContext, useEffect, useState} from "react";
import * as d3 from 'd3';

import './BpsStyles.css'
import Images from "../../assets/images/Images";
import Colors from "../../assets/colors/Colors";

import styled from "styled-components";
import BackComponentWrapper from "../RankingsAndLevelsComponent/Components/BackComponentWrapper";
import Constants from "../../data/Constants";
import StackedAreaChart from "../d3/Bps/StackedAreaChart";
import { GlobalDataContext } from "../../contexts/GlobalDataContext";
import {useTranslation} from "react-i18next";
import { groupForStacked } from "../../services/helpers";

interface Props {
    bkgColor: string,
    bkgImg: string,
    children?: React.ReactNode
}

const BpsContainerDiv = styled.div`
  display: flex;
  height: 100%;
  flex: 1 1 auto;
  flex-direction: column;
  position: relative;
  z-index: 0;

  &:after {
    content: '';
    background-attachment: fixed;
    background-image: url(${(props: Props) => props.bkgImg});
    background-position: center center;
    background-repeat: no-repeat;
    opacity: 0.1;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: -1;
  }

  &:before {
    content: '';
    background-color: ${(props: Props) => props.bkgColor};
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: -2;
  }
`;

const BpsRegionsComponent: React.FC = () => {
    return (
        <BpsContainerDiv
            bkgColor={Colors.backgroundGray}
            bkgImg={Images.mapBackgroundLife}
        >
            <BackComponentWrapper backRoute={Constants.routes.bps}/>
            <BpsRegionsContent />
        </BpsContainerDiv>
    )
};

const StyledGraphicsLegend = styled.div<{ children?: React.ReactNode }>`
    flex: 1;
    display: flex;
    justify-content: flex-start;

    & .color-label {
        width: 17px;
        height: 17px;
        margin: auto 10px;
    }

    & .text-label {
        font-family: Mapfre-Bold;
        font-size: 16px;
        margin: auto 0;
        flex: 1;
    }

    & .color-label-container-short {
        flex: 0;
        display: flex;
        justify-content: flex-end;
    }

    & .color-label-container-long {
        flex: 1;
        display: flex;
        justify-content: flex-end;
    }
`;

const Charts = styled.div<{ children?: React.ReactNode }>`
  color: white;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;

  .bps-charts__legend {
    display: flex;
    flex: 0 0 auto;
    flex-direction: row;
    justify-content: space-around;
    margin-bottom: 40px;
    margin-left: 60px;
    margin-right: 60px;
  }

  .bps-charts__columns-wrapper {
    display: flex;
    flex: 1 0 auto;
    flex-direction: row;
    margin: 0 50px;
  }

  .bps-charts__column {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    padding: 0 40px;
  }

  .bps-charts__chart-title {
    display: flex;
    flex-direction: row;
    flex: 0 0 auto;
  }

  .bps-charts__chart {
    flex: 1 0 auto;
  }

  .bps-chart-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    font-size: 1.3rem;

    & .bps-chart-header__name {
      font-family: Mapfre-Bold;
    }

    & .bps-chart-header__description {
      font-family: Mapfre-Light;
    }

    & .bps-chart-header__unit {
      display: block;
      font-family: Mapfre-Light;
      font-size: 0.85em;
      margin-top: 5px;
    }

    & .bps-chart-header__branch {
      display: flex;
      justify-content: flex-end;
      margin-left: 25px;
      text-transform: uppercase;
    }
  }

  .bps-chart {
    flex: 1 0 auto;
    position: relative;
    margin-bottom: 50px;

    & svg {
      position: absolute;
      height: 100%;
      width: 100%;
    }
  }
`;

const colors = {
    'DEVELOPED': '#D5987D',
    'BRICS': '#8EC1B6',
    'RAISING': '#B99CC4',
    'G7': '#779DCF'
};

const BpsRegionsContent: React.FC = () => {
    // @ts-ignore
    const { bpsData } = useContext(GlobalDataContext);
    const { t } = useTranslation();

    const bpsDataLife = bpsData
      .filter((a:any) => a.is_life == true)
      .map((r: any) => ({ year: r.year, bps: parseFloat(r.bps), region: r.region }));

    const bpsDataNoLife = bpsData
      .filter((a:any) => a.is_life == false)
      .map((r: any) => ({ year: r.year, bps: parseFloat(r.bps), region: r.region }));

    const stackedLife = groupForStacked(bpsDataLife);
    const stackedNoLife = groupForStacked(bpsDataNoLife);

    const maxLife = d3.max(stackedLife[stackedLife.length - 1].map(x => x[1])) || 0;
    const maxNoLife = d3.max(stackedNoLife[stackedNoLife.length - 1].map(x => x[1])) || 0;

    const yDomain = d3.scaleLinear().domain([0, Math.max(maxLife, maxNoLife)]);

    return (
        <Charts className='bps-charts'>
            <div className='bps-charts__legend'>
                <StyledGraphicsLegend>
                    <div className={'color-label-container-short'}>
                        <div className='color-label' style={{backgroundColor: colors.DEVELOPED }} />
                    </div>
                    <div className='text-label'>{t('bpsMarket.legend.1')}</div>
                </StyledGraphicsLegend>
                <StyledGraphicsLegend>
                    <div className={'color-label-container-long'}>
                        <div className='color-label' style={{backgroundColor: colors.BRICS }} />
                    </div>
                    <div className='text-label'>{t('bpsMarket.legend.2')}</div>
                </StyledGraphicsLegend>
                <StyledGraphicsLegend>
                    <div className={'color-label-container-short'}>
                        <div className='color-label' style={{backgroundColor: colors.RAISING }} />
                    </div>
                    <div className='text-label'>{t('bpsMarket.legend.3')}</div>
                </StyledGraphicsLegend>
                <StyledGraphicsLegend>
                    <div className={'color-label-container-long'}>
                        <div className='color-label' style={{backgroundColor: colors.G7 }} />
                    </div>
                    <div className='text-label'>{t('bpsMarket.legend.4')}</div>
                </StyledGraphicsLegend>
            </div>
            <div className='bps-charts__columns-wrapper'>
                <div className='bps-charts__column'>
                    <div className='bps-chart-header'>
                        <div className='bps-chart-header__title'>
                            <span className='bps-chart-header__name'>{t('bpsMarket.evolutionTitle')}</span>
                            <span className='bps-chart-header__description'>
                              {t('bpsMarket.evolutionDescription')}
                            </span>
                            <span className='bps-chart-header__unit'>
                              {t('bpsMarket.evolutionUnit')}
                            </span>
                        </div>
                        <div className='bps-chart-header__branch'>{t('lifeSelector.life')}</div>
                    </div>
                    <div className='bps-chart'>
                        <StackedAreaChart
                          bpsRecords={ bpsDataLife }
                          chartWidth={ 400 }
                          chartHeight={ 400 }
                          selectedSeries={ undefined }
                          yDomain={ yDomain }
                        />
                    </div>
                </div>

                <div className='bps-charts__column'>
                    <div className='bps-chart-header'>
                        <div className='bps-chart-header__title'>
                            <span className='bps-chart-header__name'>{t('bpsMarket.evolutionTitle')}</span>
                            <span className='bps-chart-header__description'>
                              {t('bpsMarket.evolutionDescription')}
                            </span>
                            <span className='bps-chart-header__unit'>
                              {t('bpsMarket.evolutionUnit')}
                            </span>
                        </div>
                        <div className='bps-chart-header__branch'>{t('lifeSelector.noLife')}</div>
                    </div>
                    <div className='bps-chart'>
                        <StackedAreaChart
                          bpsRecords={ bpsDataNoLife }
                          chartWidth={ 400 }
                          chartHeight={ 400 }
                          selectedSeries={ undefined }
                          yDomain={ yDomain }
                        />
                    </div>
                </div>
            </div>
        </Charts>
    )
};

export default BpsRegionsComponent;
